/**
 * Map a campaign step's `functionName` to the i18n key that labels it.
 *
 * The contact-management interaction history and the campaign message
 * chronicle both render the raw `functionName` coming from the Campaign model
 * (snake_case enum, e.g. `send_connection_request`). Those snake_case values
 * have no translations of their own — without this mapping `t(functionName)`
 * echoes the raw key back into the UI. We reuse the existing flow-editor /
 * work-unit labels so the timeline reads the same as the rest of the app.
 */
const STEP_FUNCTION_TO_LABEL_KEY: Record<string, string> = {
  send_connection_request: 'sendConnectionRequest',
  send_message: 'sendMail',
  send_inmail: 'sendInMail',
  send_email: 'sendEmail',
  send_call_transcript: 'sendCallTranscript',
  visit_profile: 'visitProfile',
  check_reply: 'checkReply',
  check_if_connected: 'workUnitCheckIfConnected',
  check_if_connected_until: 'workUnitCheckIfConnectedUntil',
  check_is_polite: 'checkIsPolite',
  check_if_generated_email_address: 'checkIfGeneratedEmailAddress',
  withdraw_connection_request: 'withdrawConnectionRequest',
  wait: 'workUnitWait',
};

/**
 * Resolve the translation key for a step function name. Unknown values
 * (including already-camelCase flow function names) pass through unchanged so
 * `t()` can still translate or fall back to them.
 */
export const stepLabelKey = (functionName?: string | null): string => {
  if (!functionName) return '';
  return STEP_FUNCTION_TO_LABEL_KEY[functionName] ?? functionName;
};

/**
 * True only for the "send connection request" step — the only step whose empty
 * body legitimately means "connection request sent without a note". Withdraw,
 * visit, wait and the various check steps also carry no text, but for them an
 * empty body is normal and must NOT be labelled as a missing note.
 */
export const isConnectionRequestStep = (functionName?: string | null): boolean =>
  functionName === 'send_connection_request' || functionName === 'sendConnectionRequest';
